import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { AspectRatio, Box, Button, Flex, HStack } from "@chakra-ui/react";

import { login } from "@/api/authService";
import { setAuthToken, getAuthToken } from "@/lib/authStorage";
import { useRouter } from "next/router";
import LoadingScreen from "@/components/LoadingScreen";
import LoginForm from "@/components/LoginForm";
import RegisterForm from "@/components/RegisterForm";

import { checkUsername, checkEmail, createUser } from "@/api/authService";
import Head from "next/head";
import GlassButton from "@/components/pressets/GlassButton";
import { FaArrowLeft } from "react-icons/fa";
import { glassContentSx } from "@/theme/glassSx";

const LoginPage = () => {
  const router = useRouter();

  const { option } = router.query;

  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [isLoadingUsername, setIsLoadingUsername] = useState(false);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [userNotFound, setUserNotFound] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isAccountCreated, setIsAccountCreated] = useState(false);
  const [isFetchingToken, setIsFetchingToken] = useState(true);

  const [isRecovering, setIsRecovering] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverySuccess, setRecoverySuccess] = useState(false);
  const [recoveryError, setRecoveryError] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      setError("");
      const data = await login(username, password);
      if (data.status === "error") {
        setIsLoading(false);
        if (data.message === "user-not-found") {
          setUserNotFound(true);
          setError("El usuario no existe. ¿Quieres crear una nueva cuenta?");
        } else if (data.message === "incorrect-password") {
          setError("Contraseña incorrecta.");
          setUserNotFound(false);
        } else {
          setError("Ha ocurrido un error desconocido.");
          setUserNotFound(false);
        }
        return;
      }
      const { token } = data;
      setUserNotFound(false);
      setAuthToken(token);
      router.push("/");
    } catch (error) {
      setIsLoading(false);
      if (!error.response) {
        setError("No se ha podido conectar al servidor.");
        return;
      }
      setError("Ha habido un problema al conectarse con el servidor.");
    }
  };

  const handlePasswordReset = async () => {
    setIsLoading(true);
    setRecoveryError("");
    setRecoverySuccess(false);

    await new Promise((r) => setTimeout(r, 1000));

    if (!recoverEmail.includes("@")) {
      setRecoveryError("No se encontró ninguna cuenta con ese correo.");
    } else {
      setRecoverySuccess(true);
    }

    setRecoverEmail("");

    setIsLoading(false);
  };

  const handleShowRegister = () => {
    setError("");
    setUsername("");
    setPassword("");
    setIsCaptchaVerified(false);
    setIsRegister(true);
    setUserNotFound(false);
  };

  const handleShowLogin = () => {
    setError("");
    setName("");
    setUsername("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setIsCaptchaVerified(false);
    setIsAccountCreated(false);
    setIsCreatingAccount(false);
    setIsRegister(false);
  };

  const handleNameValidation = () => {
    if (name.length < 6) {
      setError("El nombre debe tener al menos 6 caracteres.");
      return false;
    }
    if (!/^[a-zA-ZáéíóúÁÉÍÓÚäëïöüÄËÏÖÜñÑ.\s]*$/.test(name) || name === "") {
      setError("El nombre solo puede contener letras y no debe estar vacío.");
      return false;
    }
    return true;
  };

  const handleUsernameValidation = async () => {
    if (username.length < 6) {
      setError("El nombre de usuario debe tener al menos 6 caracteres.");
      return false;
    }

    if (!/^[a-zA-Z0-9]*$/.test(username) || username === "") {
      setError("El nombre de usuario solo puede contener letras y números.");
      return false;
    }
    setIsLoadingUsername(true);

    try {
      setIsLoadingUsername(true);
      const response = await checkUsername(username);
      if (response.message === "username-available") {
        return true;
      } else if (response.message === "username-taken") {
        setError("Este nombre de usuario ya está en uso.");
        return false;
      } else if (response.status === "error") {
        setError(response.message);
        return false;
      }
    } catch (error) {
      console.error("Error al validar el nombre de usuario.", error);
      setError("Ha ocurrido un error al validar el nombre de usuario.");
      return false;
    } finally {
      setIsLoadingUsername(false);
    }
  };

  const handleEmailValidation = async () => {
    if (!/\S+@\S+\.\S+/.test(email) || email === "") {
      setError("El correo electrónico no es válido.");
      return false;
    }

    setIsLoadingEmail(true);
    try {
      setIsLoadingEmail(true);
      const response = await checkEmail(email);
      if (response.message === "email-available") {
        return true;
      } else if (response.message === "email-taken") {
        setError("Este correo electrónico ya está en uso.");
        return false;
      } else if (response.status === "error") {
        setError(response.message);
        return false;
      }
    } catch (error) {
      console.error("Error al validar el correo electrónico.", error);
      setError("Ha ocurrido un error al validar el correo electrónico.");
      return false;
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const handlePhoneNumberValidation = () => {
    const trimmed = phoneNumber.trim();
    if (!/^09\d{8}$/.test(trimmed)) {
      setError("El número debe tener 10 dígitos y empezar con 09.");
      return false;
    }
    return true;
  };

  const handlePasswordValidation = () => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+={}\[\]:;"'<>,.?/\\|-]).{8,}$/;

    if (password.length < 8 || password === "") {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return false;
    }

    if (!passwordRegex.test(password)) {
      setError(
        "La contraseña debe tener al menos 8 caracteres, una letra mayúscula, una minúscula y un carácter especial."
      );
      return false;
    }

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return false;
    }

    return true;
  };

  const handleCaptchaVerification = () => {
    if (!isCaptchaVerified) {
      setError("Debes verificar que no eres un robot.");
      return false;
    }

    return true;
  };

  const handleRegister = async () => {
    setIsCreatingAccount(true);

    try {
      const data = {
        username,
        password,
        name,
        email,
        phoneNumber,
      };

      const response = await createUser(data);
      if (response.status === "success") {
        setIsAccountCreated(true);
        return;
      } else if (response.status === "error") {
        setError(response.message);
        setIsCreatingAccount(false);
        return;
      }
    } catch (error) {
      setIsCreatingAccount(false);
      setError("Ha ocurrido un error al crear la cuenta.");
      return;
    } finally {
      setIsCreatingAccount(false);
    }
  };

  useEffect(() => {
    if (option === "register") {
      setIsRegister(true);
    } else {
      setIsRegister(false);
    }
  }, [option]);

  useEffect(() => {
    const token = getAuthToken();

    if (token) {
      router.push("/");
    } else {
      setIsFetchingToken(false);
    }
  }, []);

  return (
    <>
      <LoadingScreen isLoading={isFetchingToken} />

      <Head>
        <title>Iniciar sesión - Codex</title>
      </Head>
 <Box
          as="nav"
          w="100%"
          bg="whiteAlpha.900"
          boxShadow="sm"
          py={{ base: 2, md: 3 }}
          px={{ base: 3, md: 6 }}
          position="fixed"
          top={0}
          left={0}
          zIndex={100}
        >
          <Flex maxW="7xl" mx="auto">
            <Flex>
              <GlassButton
                leftIcon={<FaArrowLeft />}
                colorScheme="green"
                variant="ghost"
                py={{ base: 3, md: 4 }}
                px={{ base: 4, md: 8 }}
                onClick={() => router.push("/")}
              >
                Volver
              </GlassButton>
            </Flex>
          </Flex>
        </Box>
      <Flex
        minH={{ base: "100dvh", md: "100vh" }}
        align="center"
        justify="center"
        position="relative"
        overflow="hidden"
        pt={{ base: 16, md: 0 }}
        px={{ base: 3, md: 0 }}
        bgImage="url('/assets/bg.png')"
        bgSize="cover"
        bgPosition="center"
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          backdropFilter: "blur(4px)",
          zIndex: 0,
        }}
      >
        <Flex
          w={{ base: "100%", lg: "800px" }}
          maxW="100%"
          alignItems="center"
          flexDirection={{ base: "column", lg: "row" }}
          position="relative"
          zIndex={1}
        >
          {/* Caja izquierda */}
          <motion.div
            style={{ flex: 1 }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{
              type: "spring",
            }}
          >
            
            <Box
              p={8}
              bg="brand.800"
              color="white"
              boxShadow="xl"
              borderLeftRadius="2xl"
              display={{ base: "none", lg: "flex" }}
              flexDirection="column"
              justifyContent="end"
              minH={{ base: "0", lg: "450px" }}
            >
              <Flex flex="1" align="center" justify="center">
                <AspectRatio
                  w="100%"
                  maxW="400px"
                  ratio={16 / 9}
                  borderRadius="2xl"
                  overflow="hidden"
                >
                  <iframe
                    src="https://www.youtube.com/embed/9YwVBDG719E"
                    title="YouTube video"
                    allowFullScreen
                  />
                </AspectRatio>
              </Flex>

              {!isRegister && (
                <Button
                  bg="brand.901"
                  color="brand.900"
                  _hover={{ bg: "brand.300" }}
                  size="lg"
                  borderRadius="2xl"
                  w="100%"
                  onClick={handleShowRegister}
                >
                  ¡Regístrate ahora!
                </Button>
              )}
            </Box>
          </motion.div>

          {/* Caja derecha */}
          <motion.div
            style={{ flex: 1 }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{
              type: "spring",
            }}
          >
            <Box
              p={{ base: 5, md: 8 }}
              boxShadow="overlay"
              borderRadius="2xl"
              bg="glass.solid"
              backdropFilter="blur(22px) saturate(150%)"
              border="1px solid"
              borderColor="whiteAlpha.800"
              color="text.onGlass"
              display="flex"
              flexDirection="column"
              minH={{ base: "auto", lg: "500px" }}
              maxH={{ base: "none", lg: "500px" }}
              w="100%"
              minW={0}
              overflowY="auto"
              sx={glassContentSx}
            >
              {isRegister ? (
                <RegisterForm
                  name={name}
                  setName={setName}
                  username={username}
                  setUsername={setUsername}
                  email={email}
                  setEmail={setEmail}
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                  password={password}
                  setPassword={setPassword}
                  confirmPassword={confirmPassword}
                  setConfirmPassword={setConfirmPassword}
                  setIsCaptchaVerified={setIsCaptchaVerified}
                  isCaptchaVerified={isCaptchaVerified}
                  isLoadingUsername={isLoadingUsername}
                  handleNameValidation={handleNameValidation}
                  handlePasswordValidation={handlePasswordValidation}
                  handleUsernameValidation={handleUsernameValidation}
                  handlePhoneNumberValidation={handlePhoneNumberValidation}
                  isLoadingEmail={isLoadingEmail}
                  handleEmailValidation={handleEmailValidation}
                  handleRegister={handleRegister}
                  error={error}
                  setError={setError}
                  isCreatingAccount={isCreatingAccount}
                  isAccountCreated={isAccountCreated}
                  handleShowLogin={handleShowLogin}
                />
              ) : (
                <LoginForm
                  username={username}
                  setUsername={setUsername}
                  password={password}
                  setPassword={setPassword}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  isLoading={isLoading}
                  error={error}
                  setError={setError}
                  userNotFound={userNotFound}
                  handleLogin={handleLogin}
                  handleRegister={handleShowRegister}
                  isRecovering={isRecovering}
                  setIsRecovering={setIsRecovering}
                  recoverEmail={recoverEmail}
                  setRecoverEmail={setRecoverEmail}
                  handlePasswordReset={handlePasswordReset}
                  setRecoverySuccess={setRecoverySuccess}
                  recoverySuccess={recoverySuccess}
                  recoveryError={recoveryError}
                  setRecoveryError={setRecoveryError}
                />
              )}
            </Box>
          </motion.div>
        </Flex>
      </Flex>
    </>
  );
};

LoginPage.noLayout = true;

export default LoginPage;
